import React, { useEffect, useMemo } from 'react'
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $nodesOfType,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  LexicalCommand,
  type NodeKey,
} from 'lexical'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { mergeRegister, registerNestedElementResolver } from '@lexical/utils'
import { $createMarkNode, $getMarkIDs, $isMarkNode, $unwrapMarkNode, $wrapSelectionInMarkNode, MarkNode } from '@lexical/mark'
import { nanoid } from 'nanoid'
import { CommentRelationType } from '../../../../../../../../models/MeetingSupport'
import { SidebarTitle, SidebarType } from '../../../../Sidebar/models'
import { AiResult } from '../../../../../../../../models/AiResult'
import { useAppDispatch, useAppSelector } from '../../../../../../../../store/hooks'
import { selectFilteredComments, selectNewComment, setCommentedAiResultId, setNewComment } from '../../../../../../../../store/meetingSummary/meetingSupport'
import { selectAiFeatures } from '../../../../../../../../store/meetingSummary/aiFeatures'
import { displaySidebar } from '../../../../../../../../store/meetingSummary/display'

export const INSERT_INLINE_COMMENT_COMMAND: LexicalCommand<void> = createCommand('INSERT_INLINE_COMMAND')

const QUOTE_LIMIT_REGEX = /^(.{200}[^\s]*).*/s

export const truncateQuote = (originalQuote: string) => {
  const truncatedQuote = originalQuote.replace(QUOTE_LIMIT_REGEX, '$1')
  return truncatedQuote === originalQuote ? originalQuote : `${truncatedQuote}…`
}

export interface CommentPluginProps {
  aiResult: AiResult
  basePath?: string
  path: string
}

export const CommentPlugin: React.FC<CommentPluginProps> = ({ aiResult, basePath = '$', path }) => {
  const dispatch = useAppDispatch()

  const [editor] = useLexicalComposerContext()

  const comments = useAppSelector(selectFilteredComments)
  const newComment = useAppSelector(selectNewComment)
  const aiFeatures = useAppSelector(selectAiFeatures)

  const { id: aiResultId, aiFeatureId } = aiResult

  const sidebarTitle = useMemo(() => {
    const aiFeature = aiFeatures.find((f) => f.id === aiFeatureId)
    if (aiFeature?.name) {
      return `${SidebarTitle.COMMENTS} - ${aiFeature.name}`
    }
    return SidebarTitle.COMMENTS
  }, [aiFeatureId, aiFeatures])

  useEffect(() => {
    const commentsForResult = comments.filter((comment) => {
      const {
        metadata: { basePath: commentBasePath, path: commentPath },
        relationId,
        relationType,
      } = comment
      return relationId === aiResultId && commentBasePath === basePath && commentPath === path && relationType === CommentRelationType.AI_RESULT
    })

    const getDeletableMarkIds = (markIds: string[]) => {
      const deletableMarkIds = []
      for (const markId of markIds) {
        if (
          markId !== newComment?.markId &&
          !commentsForResult.some((comment) => {
            const {
              metadata: { markId: commentMarkId },
            } = comment
            return commentMarkId === markId
          })
        ) {
          deletableMarkIds.push(markId)
        }
      }
      return deletableMarkIds
    }

    const markNodeKeyToDeletableMarkIdsMap = editor.getEditorState().read(() => {
      const markNodeKeyToDeletableMarkIdsMap: Map<NodeKey, string[]> = new Map()
      const markNodes = $nodesOfType(MarkNode)
      for (const markNode of markNodes) {
        const markIds = markNode.getIDs()
        const deletableMarkIds = getDeletableMarkIds(markIds)
        if (!deletableMarkIds.length) {
          continue
        }
        markNodeKeyToDeletableMarkIdsMap.set(markNode.getKey(), deletableMarkIds)
      }
      return markNodeKeyToDeletableMarkIdsMap
    })

    if (markNodeKeyToDeletableMarkIdsMap.size) {
      setTimeout(() => {
        editor.update(() => {
          for (const [nodeKey, deletableMarkIds] of markNodeKeyToDeletableMarkIdsMap) {
            const node: null | MarkNode = $getNodeByKey(nodeKey)
            if ($isMarkNode(node)) {
              for (const deletableMarkId of deletableMarkIds) {
                node.deleteID(deletableMarkId)
                if (node.getIDs().length === 0) {
                  $unwrapMarkNode(node)
                }
              }
            }
          }
        })
      })
    }
  }, [editor, newComment, aiResultId, basePath, path, comments])

  useEffect(() => {
    return mergeRegister(
      registerNestedElementResolver<MarkNode>(
        editor,
        MarkNode,
        (from: MarkNode) => {
          return $createMarkNode(from.getIDs())
        },
        (from: MarkNode, to: MarkNode) => {
          // Merge the IDs
          const ids = from.getIDs()
          ids.forEach((id) => {
            to.addID(id)
          })
        },
      ),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          const selection = $getSelection()

          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode()

            if ($isTextNode(anchorNode)) {
              const commentIDs = $getMarkIDs(anchorNode, selection.anchor.offset)
              if (commentIDs !== null) {
                // todo highlight commentIds
                if (!editor.isEditable()) {
                  dispatch(setCommentedAiResultId(aiResultId))
                  dispatch(displaySidebar({ type: SidebarType.COMMENTS, title: sidebarTitle, icon: 'bi-chat-square-text' }))
                }
              }
            }
          }
        })
      }),
      editor.registerCommand(
        INSERT_INLINE_COMMENT_COMMAND,
        (_, editor) => {
          const selection = $getSelection()
          const domSelection = window.getSelection()
          if (domSelection !== null) {
            domSelection.removeAllRanges()
          }
          editor.update(() => {
            if ($isRangeSelection(selection)) {
              const originalQuote = editor.getEditorState().read(() => selection.getTextContent())
              const quote = truncateQuote(originalQuote)
              const isBackward = selection.isBackward()
              const markId = nanoid()
              $wrapSelectionInMarkNode(selection, isBackward, markId)
              const newComment = { markId, quote, aiResultId, basePath, path }
              dispatch(setCommentedAiResultId(aiResultId))
              dispatch(setNewComment(newComment))
              dispatch(displaySidebar({ type: SidebarType.COMMENTS, title: sidebarTitle, icon: 'bi-chat-square-text' }))
            }
          })
          return true
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    )
  }, [dispatch, aiResultId, basePath, path, editor, sidebarTitle])

  return null
}
